import { useState, useEffect } from 'react';
import { useAuth } from '@/context/auth';
import { useCoins } from '@/hooks/useCoins';
import CareerPathVisualizer from '@/components/career/CareerPathVisualizer';
import { EnhancedLearningPaths } from '@/components/learning/EnhancedLearningPaths';
import { PurchaseModal } from '@/components/shared/PurchaseModal';
import { Button } from '@/components/ui/button';
import { Lock } from 'lucide-react';
import { PurchaseSuccess } from '@/components/shared/PurchaseSuccess';
import { updateProgress } from '@/services/progressTracking';
import { Analytics } from '@/components/career/Analytics';
import { useLearning } from '@/hooks/useLearning';

export function CareerPathsPage() {
  const { user } = useAuth();
  const { coins, purchaseItem } = useCoins();
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [hasPersonalPath, setHasPersonalPath] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);

  const handlePurchase = async () => {
    try {
      await purchaseItem({
        id: 'personal-career-path',
        title: 'Personal Career Path',
        cost: 250,
        description: 'Unlock your personalized AI-powered career path'
      });
      setShowPurchaseModal(false);
      setShowSuccess(true);
      setHasPersonalPath(true);
    } catch (error) {
      console.error('Purchase failed:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchAnalytics();
    }
  }, [user]);

  const fetchAnalytics = async () => {
    const { data } = await supabase
      .from('career_progress')
      .select('*')
      .eq('user_id', user.id);
    
    setAnalytics(processAnalyticsData(data));
  };

  return (
    <div className="container mx-auto py-8 px-4 space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Career Paths</h1>
        {!hasPersonalPath && (
          <Button
            onClick={() => setShowPurchaseModal(true)}
            className="flex items-center gap-2"
          >
            <Lock className="w-4 h-4" />
            Unlock Personal Path
          </Button>
        )}
      </div>

      {/* Generic Career Path Visualizer */}
      {!hasPersonalPath && (
        <div className="mb-8">
          <CareerPathVisualizer />
          
          {/* Premium Feature CTA */}
          <div className="mt-8 p-6 bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl text-center">
            <h3 className="text-xl font-bold text-primary-900 mb-2">
              Want a Personalized Career Path?
            </h3>
            <p className="text-primary-700 mb-4">
              Get AI-powered recommendations tailored to your skills and goals
            </p>
            <Button
              onClick={() => setShowPurchaseModal(true)}
              className="bg-primary-600 hover:bg-primary-700 text-white"
            >
              Unlock Personal Path (250 coins)
            </Button>
          </div>
        </div>
      )}

      {/* Personal Learning Paths */}
      {hasPersonalPath && user?.profile && (
        <EnhancedLearningPaths profile={user.profile} />
      )}

      {/* Analytics Section */}
      {hasPersonalPath && analytics && (
        <Analytics data={analytics} />
      )}

      {/* Purchase Modal */}
      <PurchaseModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        onConfirm={handlePurchase}
        item={{
          title: 'Personal Career Path',
          description: 'Unlock AI-powered personalized career guidance',
          cost: 250,
          currentCoins: coins
        }}
      />

      <PurchaseSuccess 
        show={showSuccess}
        onComplete={() => setShowSuccess(false)}
      />
    </div>
  );
} 